import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { useUserDocument } from './UserDocumentContext';
import { positionEventEmitter } from '../services/positionEventEmitter';
import { SimplifiedBalanceService, SimplifiedBalanceData } from '../services/SimplifiedBalanceService';

export interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'trade_pnl' | 'fee';
  amount: number;
  balance: number;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  method?: string;
  description: string;
  timestamp: Date;
  reference?: string;
}

export interface AccountDetails {
  accountId: string;
  accountType: 'demo' | 'live';
  status: 'active' | 'suspended' | 'pending';
  kycStatus: 'pending' | 'verified' | 'rejected';
  userType: 'standard' | 'premium' | 'vip';
  createdAt: Date;
  lastLogin: Date;
}

export interface AccountBalance {
  demoBalance: number; // Base account balance (deposits - withdrawals + P&L)
  liveBalance: number; // Base account balance (deposits - withdrawals + P&L)
  equity: number; // Balance + Unrealized P/L
  margin: number; // Total margin used for all open positions
  freeMargin: number; // Total balance - margin (available for new trades)
  marginLevel: number; // (Equity / Margin) * 100
  profit: number; // Overall realized profit/loss from all closed positions
  leverage: string;
  totalBalance: number; // Base balance + realized profit (what shows as "Balance" in header)
  currentAccountType: 'demo' | 'live'; // Track which account the global values belong to
}

interface AccountContextType {
  accountBalance: AccountBalance;
  transactions: Transaction[];
  accountDetails: AccountDetails | null;
  isLoading: boolean;
  balanceUpdateTrigger: number;
  setBalanceUpdateTrigger: (value: number | ((prev: number) => number)) => void;
  deposit: (amount: number, method: string) => Promise<Transaction>;
  withdraw: (amount: number, method: string) => Promise<Transaction>;
  getTransactions: () => Promise<Transaction[]>;
  getAccountDetails: () => Promise<AccountDetails>;
  updateBalance: (type: 'demo' | 'live', amount: number | ((currentBalance: number) => number)) => Promise<void>;
  addProfit: (pnlAmount: number, marginReturn: number, description: string, accountType?: 'demo' | 'live') => Promise<void>;
  updateUsedMargin: (usedMargin: number, accountType?: 'demo' | 'live') => Promise<void>;
  updateEquity: (unrealizedPnL: number, accountType?: 'demo' | 'live') => void;
  getAccountBalance: (accountType: 'demo' | 'live') => AccountBalance;
  forceFirebaseSync: () => void;
  refillDemoAccount: () => Promise<void>;
  // Simplified balance methods
  getSimplifiedBalance: (accountType: 'demo' | 'live') => Promise<SimplifiedBalanceData | null>;
  openPosition: (accountType: 'demo' | 'live', marginRequired: number, positionId: string, symbol: string) => Promise<SimplifiedBalanceData>;
  closePosition: (accountType: 'demo' | 'live', marginReleased: number, pnl: number, positionId: string, symbol: string) => Promise<SimplifiedBalanceData>;
  processDeposit: (accountType: 'demo' | 'live', amount: number, description: string) => Promise<SimplifiedBalanceData>;
  processWithdrawal: (accountType: 'demo' | 'live', amount: number, description: string) => Promise<SimplifiedBalanceData>;
  resetLiveAccountBalance: () => Promise<void>;
  updateSimplifiedEquity: (accountType: 'demo' | 'live', unrealizedPnL: number) => Promise<SimplifiedBalanceData | null>;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};

interface AccountProviderProps {
  children: ReactNode;
}

export const AccountProvider: React.FC<AccountProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { demoAccountData, realAccountData, updateAccountSummary } = useUserDocument();
  const [accountBalance, setAccountBalance] = useState<AccountBalance>({
    demoBalance: 0, // Start with 0.00 until loaded from Firestore
    liveBalance: 0, // Start with 0.00 until loaded from Firestore
    equity: 0, // Start with 0.00 until loaded from Firestore
    margin: 0,
    freeMargin: 0, // Start with 0.00 until loaded from Firestore
    marginLevel: 0,
    profit: 0, // Overall realized profit/loss
    leverage: '100:1',
    totalBalance: 0, // Start with 0.00 until loaded from Firestore
    currentAccountType: 'demo', // Track which account the global values belong to
  });
  
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accountDetails, setAccountDetails] = useState<AccountDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [balanceUpdateTrigger, setBalanceUpdateTrigger] = useState(0);
  
  // Track when balance was last updated locally to prevent Firestore sync from overwriting recent changes
  const lastBalanceUpdateRef = useRef<number>(0);

  // Subscribe to SimplifiedBalanceService updates
  useEffect(() => {
    const { SimplifiedBalanceService } = require('../services/SimplifiedBalanceService');
    
    const unsubscribe = SimplifiedBalanceService.onBalanceUpdate(() => {
      setBalanceUpdateTrigger(prev => prev + 1);
    });
    
    return unsubscribe;
  }, []);

  // Sync balance from Firestore when account data loads (but not if recently updated locally)
  useEffect(() => {
    if (demoAccountData && realAccountData) {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastBalanceUpdateRef.current;
      
      // Only sync from Firestore if balance wasn't updated locally in the last 5 seconds
      // AND if there are no open positions (to prevent overwriting correct balance with stale data)
      // AND if the Firestore balance is significantly different (indicating a real update)
      if (timeSinceLastUpdate > 5000 && accountBalance.margin === 0) {
        const demoBalance = demoAccountData.accountSummary?.balance?.totalBalance || 1000;
        const realBalance = realAccountData.accountSummary?.balance?.totalBalance || 0;
        
        // Only sync if the Firestore balance is significantly different from local balance
        // This prevents overwriting correct local balance with stale Firestore data
        const balanceDifference = Math.abs(demoBalance - accountBalance.demoBalance);
        if (balanceDifference < 0.01) { // Less than 1 cent difference, skip sync
          return;
        }
        
        // Always sync from Firestore when there's a significant difference
        // This ensures we get the correct balance from the database
        const demoMargin = demoAccountData.accountSummary?.margin?.usedMargin || 0;
        const realMargin = realAccountData.accountSummary?.margin?.usedMargin || 0;
        const demoEquity = demoAccountData.accountSummary?.equity?.totalEquity || demoBalance;
        const realEquity = realAccountData.accountSummary?.equity?.totalEquity || realBalance;
        const demoFreeMargin = demoAccountData.accountSummary?.margin?.freeMargin || demoBalance;
        const realFreeMargin = realAccountData.accountSummary?.margin?.freeMargin || realBalance;
        const demoMarginLevel = demoAccountData.accountSummary?.margin?.marginLevel || 0;
        const realMarginLevel = realAccountData.accountSummary?.margin?.marginLevel || 0;
        const demoPnL = demoAccountData.accountSummary?.pnl?.realizedPnL || 0;
        const realPnL = realAccountData.accountSummary?.pnl?.realizedPnL || 0;

        setAccountBalance(prev => ({
          ...prev,
          demoBalance: demoBalance,
          liveBalance: realBalance,
          equity: demoBalance, // Use demo equity for now
          margin: demoMargin, // Use demo margin for now
          freeMargin: demoFreeMargin, // Use demo free margin for now
          marginLevel: demoMarginLevel, // Use demo margin level for now
          profit: demoPnL, // Use demo P&L for now
          totalBalance: demoBalance, // Use demo balance for now
        }));

      } else {
      }
    }
  }, [demoAccountData, realAccountData, accountBalance.margin]);

  // Listen to position updates and update account balance accordingly
  useEffect(() => {
    const handlePositionUpdate = (data: { totalUsedMargin: number; totalUnrealizedPnL: number; accountType: string }) => {
      setAccountBalance(prev => {
        const baseBalance = data.accountType === 'demo' ? prev.demoBalance : prev.liveBalance;

        // 1. Total Balance = Base balance (already includes returned margin + P&L from closed positions)
        const newTotalBalance = baseBalance;

        // 2. Equity = Balance + Unrealized P/L
        const newEquity = newTotalBalance + data.totalUnrealizedPnL;

        // 3. Margin = total margin for all open positions
        const newMargin = data.totalUsedMargin;

        // 4. Free Margin = Equity - Margin
        const newFreeMargin = newEquity - newMargin;

        // 5. Margin Level = (Equity / Margin) * 100
        const newMarginLevel = newMargin > 0 ? (newEquity / newMargin) * 100 : 0;


        // Always update margin, equity, free margin, and margin level from position updates
        // This ensures margin is correctly updated when positions are opened/closed
        return {
          ...prev,
          equity: newEquity,
          margin: newMargin, // Always update margin from position updates
          freeMargin: Math.max(0, newFreeMargin), // Ensure free margin doesn't go negative
          marginLevel: newMarginLevel,
          currentAccountType: data.accountType as 'demo' | 'live', // Track which account these values belong to
        };
      });
    };

    positionEventEmitter.on('positionsUpdated', handlePositionUpdate);

    return () => {
      positionEventEmitter.off('positionsUpdated', handlePositionUpdate);
    };
  }, []);

  // Mock transactions data
  useEffect(() => {
    if (user) {
      const mockTransactions: Transaction[] = [
        {
          id: '1',
          type: 'deposit',
          amount: 5000,
          balance: 25000,
          status: 'completed',
          method: 'Bank Transfer',
          description: 'Initial deposit via bank transfer',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
          reference: 'TXN-001',
        },
        {
          id: '2',
          type: 'trade_pnl',
          amount: 42,
          balance: 24850,
          status: 'completed',
          description: 'EURUSD position closed with profit',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000), // 3 hours ago
        },
        {
          id: '3',
          type: 'withdrawal',
          amount: 2500,
          balance: 22500,
          status: 'completed',
          method: 'Bank Transfer',
          description: 'Withdrawal to bank account',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
          reference: 'TXN-002',
        },
        {
          id: '4',
          type: 'trade_pnl',
          amount: -125,
          balance: 22375,
          status: 'completed',
          description: 'GBPUSD position closed with loss',
          timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
        },
        {
          id: '5',
          type: 'deposit',
          amount: 3000,
          balance: 25375,
          status: 'completed',
          method: 'UPI',
          description: 'Additional deposit via UPI',
          timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
          reference: 'TXN-003',
        },
        {
          id: '6',
          type: 'fee',
          amount: 25,
          balance: 25350,
          status: 'completed',
          description: 'Monthly account maintenance fee',
          timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 1 week ago
        },
        {
          id: '7',
          type: 'trade_pnl',
          amount: 89,
          balance: 25439,
          status: 'completed',
          description: 'BTC position closed with profit',
          timestamp: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000), // 8 days ago
        },
        {
          id: '8',
          type: 'deposit',
          amount: 1500,
          balance: 26939,
          status: 'completed',
          method: 'Cryptocurrency',
          description: 'Deposit via Bitcoin transfer',
          timestamp: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000), // 10 days ago
          reference: 'TXN-004',
        },
        {
          id: '9',
          type: 'trade_pnl',
          amount: -67,
          balance: 26872,
          status: 'completed',
          description: 'GOLD position closed with loss',
          timestamp: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000), // 12 days ago
        },
        {
          id: '10',
          type: 'withdrawal',
          amount: 1000,
          balance: 25872,
          status: 'completed',
          method: 'PayPal',
          description: 'Withdrawal to PayPal account',
          timestamp: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000), // 15 days ago
          reference: 'TXN-005',
        },
        {
          id: '11',
          type: 'fee',
          amount: 15,
          balance: 25857,
          status: 'completed',
          description: 'Trading platform usage fee',
          timestamp: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000), // 20 days ago
        },
      ];
      setTransactions(mockTransactions);
    }
  }, [user]);

  // Mock account details
  useEffect(() => {
    if (user) {
      const mockAccountDetails: AccountDetails = {
        accountId: 'LTS-2024-001',
        accountType: 'live',
        status: 'active',
        kycStatus: 'verified',
        userType: 'standard',
        createdAt: new Date('2024-01-01'),
        lastLogin: new Date(),
      };
      setAccountDetails(mockAccountDetails);
    }
  }, [user]);

  const deposit = async (amount: number, method: string): Promise<Transaction> => {
    setIsLoading(true);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newTransaction: Transaction = {
        id: Date.now().toString(),
        type: 'deposit',
        amount,
        balance: accountBalance.liveBalance + amount,
        status: 'completed',
        method,
        description: `Deposit via ${method}`,
        timestamp: new Date(),
        reference: `TXN-${Date.now()}`,
      };

      // Update balance
      const newBalance = accountBalance.liveBalance + amount;
      setAccountBalance(prev => ({
        ...prev,
        liveBalance: newBalance,
        totalBalance: newBalance + prev.profit, // Update total balance
        equity: prev.equity + amount,
        freeMargin: prev.freeMargin + amount,
      }));

      // Add transaction
      setTransactions(prev => [newTransaction, ...prev]);

      return newTransaction;
    } finally {
      setIsLoading(false);
    }
  };

  const withdraw = async (amount: number, method: string): Promise<Transaction> => {
    setIsLoading(true);
    
    try {
      // Check if sufficient balance
      if (amount > accountBalance.liveBalance) {
        throw new Error('Insufficient balance');
      }

      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newTransaction: Transaction = {
        id: Date.now().toString(),
        type: 'withdrawal',
        amount,
        balance: accountBalance.liveBalance - amount,
        status: 'completed',
        method,
        description: `Withdrawal via ${method}`,
        timestamp: new Date(),
        reference: `TXN-${Date.now()}`,
      };

      // Update balance
      const newBalance = accountBalance.liveBalance - amount;
      setAccountBalance(prev => ({
        ...prev,
        liveBalance: newBalance,
        totalBalance: newBalance + prev.profit, // Update total balance
        equity: prev.equity - amount,
        freeMargin: prev.freeMargin - amount,
      }));

      // Add transaction
      setTransactions(prev => [newTransaction, ...prev]);

      return newTransaction;
    } finally {
      setIsLoading(false);
    }
  };

  const getTransactions = async (): Promise<Transaction[]> => {
    // In a real app, this would fetch from API
    return transactions;
  };

  const getAccountDetails = async (): Promise<AccountDetails> => {
    if (!accountDetails) {
      throw new Error('Account details not found');
    }
    return accountDetails;
  };

  const updateBalance = async (type: 'demo' | 'live', amount: number | ((currentBalance: number) => number)) => {
    // Update timestamp to prevent Firestore sync from overwriting this change
    lastBalanceUpdateRef.current = Date.now();
    
    if (type === 'demo') {
      setAccountBalance(prev => {
        const currentBalance = prev.demoBalance;
        const newAmount = typeof amount === 'function' ? amount(currentBalance) : amount;
        
        // Update Firestore and wait for completion
        updateAccountSummary('demo', {
          balance: {
            totalBalance: newAmount,
            availableBalance: newAmount,
            lockedBalance: 0,
            currency: 'USD'
          },
          margin: {
            freeMargin: newAmount,
            usedMargin: prev.margin,
            marginLevel: prev.margin > 0 ? (newAmount / prev.margin) * 100 : 0,
            marginCallLevel: 100,
            stopOutLevel: 20
          },
          equity: {
            totalEquity: newAmount,
            freeEquity: newAmount,
            usedEquity: prev.margin
          }
        }).then(() => {
        }).catch(error => {
          // console.error('❌ Error updating demo balance in Firestore:', error);
        });
        
        return {
          ...prev,
          demoBalance: newAmount,
          totalBalance: newAmount, // Total balance = base balance
          equity: newAmount, // For demo, equity = balance (no unrealized P&L)
          freeMargin: newAmount, // Free margin is the available balance
          marginLevel: prev.margin > 0 ? (newAmount / prev.margin) * 100 : 0,
        };
      });
    } else {
      setAccountBalance(prev => {
        const currentBalance = prev.liveBalance;
        const newAmount = typeof amount === 'function' ? amount(currentBalance) : amount;
        
        // Update Firestore and wait for completion
        updateAccountSummary('real', {
          balance: {
            totalBalance: newAmount,
            availableBalance: newAmount,
            lockedBalance: 0,
            currency: 'USD'
          },
          margin: {
            freeMargin: newAmount,
            usedMargin: prev.margin,
            marginLevel: prev.margin > 0 ? (newAmount / prev.margin) * 100 : 0,
            marginCallLevel: 100,
            stopOutLevel: 20
          },
          equity: {
            totalEquity: newAmount,
            freeEquity: newAmount,
            usedEquity: prev.margin
          }
        }).then(() => {
        }).catch(error => {
          // console.error('❌ Error updating real balance in Firestore:', error);
        });
        
        return {
          ...prev,
          liveBalance: newAmount,
          totalBalance: newAmount, // Total balance = base balance
          equity: newAmount, // For simplicity, equity = balance (no unrealized P&L)
          freeMargin: newAmount, // Free margin is the available balance
          marginLevel: prev.margin > 0 ? (newAmount / prev.margin) * 100 : 0,
        };
      });
    }

    // Force Firebase sync after balance update to ensure consistency
    // Wait longer for Firestore to update, then allow sync
    setTimeout(() => {
      lastBalanceUpdateRef.current = 0; // Reset to allow Firebase sync
    }, 3000); // 3 second delay to allow Firestore to fully update
  };

  const updateUsedMargin = useCallback(async (usedMargin: number, accountType: 'demo' | 'live' = 'demo') => {
    setAccountBalance(prev => {
      const newFreeMargin = prev.equity - usedMargin;
      const newMarginLevel = usedMargin > 0 ? (prev.equity / usedMargin) * 100 : 0;
      
      // Update Firestore
      updateAccountSummary(accountType, {
        margin: {
          freeMargin: Math.max(0, newFreeMargin),
          usedMargin: usedMargin,
          marginLevel: newMarginLevel,
          marginCallLevel: 100,
          stopOutLevel: 20
        },
        equity: {
          totalEquity: prev.equity,
          freeEquity: Math.max(0, newFreeMargin),
          usedEquity: usedMargin
        }
      }).catch(error => {
        console.error(`Error updating ${accountType} margin in Firestore:`, error);
      });
      
      return {
        ...prev,
        margin: usedMargin,
        freeMargin: Math.max(0, newFreeMargin),
        marginLevel: newMarginLevel,
      };
    });
  }, [updateAccountSummary]);

  const updateEquity = useCallback((unrealizedPnL: number, accountType: 'demo' | 'live' = 'live') => {
    setAccountBalance(prev => {
      const currentBalance = accountType === 'demo' ? prev.demoBalance : prev.liveBalance;
      const newEquity = currentBalance + unrealizedPnL;
      const newFreeMargin = newEquity - prev.margin;
      const newMarginLevel = prev.margin > 0 ? (newEquity / prev.margin) * 100 : 0;
      
      return {
        ...prev,
        equity: newEquity,
        freeMargin: Math.max(0, newFreeMargin),
        marginLevel: newMarginLevel,
      };
    });
  }, []);

  const addProfit = async (pnlAmount: number, marginReturn: number, description: string, accountType: 'demo' | 'live' = 'live') => {
    // Update timestamp to prevent Firestore sync from overwriting this change
    lastBalanceUpdateRef.current = Date.now();
    
    // Update balance and profit
    setAccountBalance(prev => {
      const baseBalance = accountType === 'demo' ? prev.demoBalance : prev.liveBalance;
      const currentMargin = prev.margin;
      
      // When position closes: return margin + add/subtract P&L to balance
      // The base balance should be updated with ONLY the P&L (margin is just released, not added to balance)
      const newBaseBalance = baseBalance + pnlAmount; // Add/subtract P&L to base balance (margin is just released)
      const newProfit = prev.profit + pnlAmount; // Add P&L to overall profit
      const newTotalBalance = newBaseBalance; // Total balance = base balance (includes P&L, margin is just released)
      
      // Reduce margin by the returned margin amount (since position is closed)
      const newMargin = Math.max(0, currentMargin - marginReturn);
      
      // Calculate new free margin and margin level
      const newFreeMargin = newBaseBalance - newMargin;
      const newMarginLevel = newMargin > 0 ? (newBaseBalance / newMargin) * 100 : 0;
      
      
      // Update Firestore and wait for completion
      updateAccountSummary(accountType, {
        balance: {
          totalBalance: newBaseBalance,
          availableBalance: newBaseBalance, // Available balance = total balance when margin is 0
          lockedBalance: 0,
          currency: 'USD'
        },
        pnl: {
          totalPnL: newProfit,
          dailyPnL: 0, // Could be calculated separately
          weeklyPnL: 0,
          monthlyPnL: 0,
          yearlyPnL: 0,
          unrealizedPnL: 0,
          realizedPnL: newProfit
        },
        margin: {
          freeMargin: Math.max(0, newFreeMargin),
          usedMargin: newMargin,
          marginLevel: newMarginLevel,
          marginCallLevel: 100,
          stopOutLevel: 20
        },
        equity: {
          totalEquity: newBaseBalance,
          freeEquity: Math.max(0, newFreeMargin),
          usedEquity: newMargin
        }
      }).then(() => {
      }).catch(error => {
        // console.error(`❌ Error updating ${accountType} profit in Firestore:`, error);
      });
      
      const updatedBalance = {
        ...prev,
        ...(accountType === 'demo' 
          ? { demoBalance: newBaseBalance }
          : { liveBalance: newBaseBalance }
        ),
        totalBalance: newTotalBalance,
        profit: newProfit,
        margin: newMargin, // Update margin to reflect closed position
        freeMargin: Math.max(0, newFreeMargin), // Free margin = balance - used margin
        marginLevel: newMarginLevel,
        equity: newBaseBalance, // Equity = balance (no unrealized P&L when position is closed)
      };

      
      // Trigger a re-render to ensure header updates immediately
      setBalanceUpdateTrigger(prev => prev + 1);
      
      return updatedBalance;
    });

    // Add transaction record
    const newTransaction: Transaction = {
      id: Date.now().toString(),
      type: 'trade_pnl',
      amount: pnlAmount, // Only record the P&L amount
      balance: accountType === 'demo' 
        ? accountBalance.demoBalance + marginReturn + pnlAmount
        : accountBalance.liveBalance + marginReturn + pnlAmount,
      status: 'completed',
      description,
      timestamp: new Date(),
    };

    setTransactions(prev => [newTransaction, ...prev]);

    // Force Firebase sync after position closing to ensure consistency
    // Wait longer for Firestore to update, then allow sync
    setTimeout(() => {
      lastBalanceUpdateRef.current = 0; // Reset to allow Firebase sync
    }, 5000); // 5 second delay to allow Firestore to fully update
  };

  // Memoized demo account balance
  const demoAccountBalance = useMemo(() => {
    const accountData = demoAccountData;
    
    // Always use Firestore data as the source of truth for demo account
    const firestoreTotalBalance = accountData?.accountSummary?.balance?.totalBalance || 1000;
    const firestoreAvailableBalance = accountData?.accountSummary?.balance?.availableBalance || firestoreTotalBalance;
    const firestoreMargin = accountData?.accountSummary?.margin?.usedMargin || 0;
    const firestoreFreeMargin = accountData?.accountSummary?.margin?.freeMargin || firestoreAvailableBalance;
    const firestoreMarginLevel = accountData?.accountSummary?.margin?.marginLevel || (firestoreMargin > 0 ? (firestoreAvailableBalance / firestoreMargin) * 100 : 0);
    const firestoreEquity = accountData?.accountSummary?.equity?.totalEquity || firestoreTotalBalance;
    const firestoreRealizedPnL = accountData?.accountSummary?.pnl?.realizedPnL || 0;
    
    // Use available balance when margin is locked, total balance when no margin
    const displayBalance = firestoreMargin > 0 ? firestoreAvailableBalance : firestoreTotalBalance;
    
    
    return {
      demoBalance: displayBalance, // Use available balance when margin is locked
      liveBalance: accountBalance.liveBalance,
      equity: firestoreEquity,
      margin: firestoreMargin,
      freeMargin: firestoreFreeMargin,
      marginLevel: firestoreMarginLevel,
      profit: firestoreRealizedPnL,
      leverage: '100:1',
      totalBalance: displayBalance, // Use available balance when margin is locked
      currentAccountType: accountBalance.currentAccountType,
    };
  }, [accountBalance, demoAccountData, balanceUpdateTrigger]);

  // Memoized live account balance
  const liveAccountBalance = useMemo(() => {
    const accountData = realAccountData;
    
    // Always use Firestore data as the source of truth for live account
    const firestoreTotalBalance = accountData?.accountSummary?.balance?.totalBalance || 0;
    const firestoreAvailableBalance = accountData?.accountSummary?.balance?.availableBalance || firestoreTotalBalance;
    const firestoreMargin = accountData?.accountSummary?.margin?.usedMargin || 0;
    const firestoreFreeMargin = accountData?.accountSummary?.margin?.freeMargin || firestoreAvailableBalance;
    const firestoreMarginLevel = accountData?.accountSummary?.margin?.marginLevel || (firestoreMargin > 0 ? (firestoreAvailableBalance / firestoreMargin) * 100 : 0);
    const firestoreEquity = accountData?.accountSummary?.equity?.totalEquity || firestoreTotalBalance;
    const firestoreRealizedPnL = accountData?.accountSummary?.pnl?.realizedPnL || 0;
    
    // Use available balance when margin is locked, total balance when no margin
    const displayBalance = firestoreMargin > 0 ? firestoreAvailableBalance : firestoreTotalBalance;
    
    
    return {
      demoBalance: accountBalance.demoBalance,
      liveBalance: displayBalance, // Use available balance when margin is locked
      equity: firestoreEquity,
      margin: firestoreMargin,
      freeMargin: firestoreFreeMargin,
      marginLevel: firestoreMarginLevel,
      profit: firestoreRealizedPnL,
      leverage: '100:1',
      totalBalance: displayBalance, // Use available balance when margin is locked
      currentAccountType: accountBalance.currentAccountType,
    };
  }, [accountBalance, realAccountData, balanceUpdateTrigger]);

  const getAccountBalance = useCallback((accountType: 'demo' | 'live'): AccountBalance => {
    return accountType === 'demo' ? demoAccountBalance : liveAccountBalance;
  }, [demoAccountBalance, liveAccountBalance]);

  const forceFirebaseSync = useCallback(() => {
    lastBalanceUpdateRef.current = 0; // Reset to allow Firebase sync
  }, []);

  const refillDemoAccount = useCallback(async () => {
    try {
      await updateBalance('demo', 1000);
    } catch (error) {
      console.error('❌ Error refilling demo account:', error);
      throw error;
    }
  }, [updateBalance]);

  // Simplified balance methods
  const getSimplifiedBalance = useCallback(async (accountType: 'demo' | 'live') => {
    if (!user) return null;
    return await SimplifiedBalanceService.getBalanceFromFirestore(user.uid, accountType);
  }, [user]);

  const openPosition = useCallback(async (accountType: 'demo' | 'live', marginRequired: number, positionId: string, symbol: string) => {
    if (!user) throw new Error('User not authenticated');
    return await SimplifiedBalanceService.onPositionOpen(user.uid, accountType, marginRequired, positionId, symbol);
  }, [user]);

  const closePosition = useCallback(async (accountType: 'demo' | 'live', marginReleased: number, pnl: number, positionId: string, symbol: string) => {
    if (!user) throw new Error('User not authenticated');
    return await SimplifiedBalanceService.onPositionClose(user.uid, accountType, marginReleased, pnl, positionId, symbol);
  }, [user]);

  const processDeposit = useCallback(async (accountType: 'demo' | 'live', amount: number, description: string) => {
    if (!user) throw new Error('User not authenticated');
    return await SimplifiedBalanceService.processDeposit(user.uid, accountType, amount, description);
  }, [user]);

  const processWithdrawal = useCallback(async (accountType: 'demo' | 'live', amount: number, description: string) => {
    if (!user) throw new Error('User not authenticated');
    return await SimplifiedBalanceService.processWithdrawal(user.uid, accountType, amount, description);
  }, [user]);

  const updateSimplifiedEquity = useCallback(async (accountType: 'demo' | 'live', unrealizedPnL: number) => {
    if (!user) throw new Error('User not authenticated');
    return await SimplifiedBalanceService.updateEquity(user.uid, accountType, unrealizedPnL);
  }, [user]);

  const resetLiveAccountBalance = useCallback(async (): Promise<void> => {
    if (!user) return;
    
    try {
      const { SimplifiedBalanceService } = await import('../services/SimplifiedBalanceService');
      await SimplifiedBalanceService.resetLiveAccountBalance(user.uid);
      
      // Trigger balance update
      setBalanceUpdateTrigger(prev => prev + 1);
      
      // console.log('✅ Live account balance reset to $0.00');
    } catch (error) {
      console.error('❌ Error resetting live account balance:', error);
    }
  }, [user]);

  const value: AccountContextType = {
    accountBalance,
    transactions,
    accountDetails,
    isLoading,
    balanceUpdateTrigger,
    setBalanceUpdateTrigger,
    deposit,
    withdraw,
    getTransactions,
    getAccountDetails,
    updateBalance,
    addProfit,
    updateUsedMargin,
    updateEquity,
    getAccountBalance,
    forceFirebaseSync,
    refillDemoAccount,
    // Simplified balance methods
    getSimplifiedBalance,
    openPosition,
    closePosition,
    processDeposit,
    processWithdrawal,
    updateSimplifiedEquity,
    resetLiveAccountBalance,
  };

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};
